import {
  ContactBookGroupType,
  FormEntityType,
  InvitationStatus,
  NetworkingModel,
  OrderItemStatus,
  OrderStatus,
  ParticipantGroupType,
  AvailabilitiesModel,
  prisma,
  type Prisma,
  type Profile,
  FormEntity,
  ParticipantType,
  NetworkingType,
  OrderType,
} from "client";
import { getEvent } from "services/event";
import { createDefaultAvailabilities } from "services/availability";
import { createDefaultNetworkingSettings } from "services/networkingSettings";
import { createParticipantFormTemplate } from "services/formTemplates";
import { getEventLanguagesByEventIdOrEventSlug } from "services/language";
import {
  findOrCreateScopedUser,
  WHITE_LABEL_REQUIRED_MESSAGE,
} from "services/user/scopedUser";
import { flattenTicketWithBookableItem } from "../bookableItem/helpers";
import jwt from "jsonwebtoken";
import { getParticipantGroupsByOrderId } from "services/participantGroup";
import {
  EVENT_PROFILE_EMAIL_TAKEN,
  eventProfileEmailConflicts,
} from "./event-profile-email.pure";

export {
  collectInvitationEmails,
  pickPreviousInvitationEmail,
} from "./event-profile-email.pure";
import { invalidateCheckinRosterForProfile } from "services/checkInOut/warmCheckinCache";
import {
  CreateProfileV2Result,
  DEFAULT_FIELDS,
  findExistingProfile,
  normalizeProfileFieldValue,
  ProfileData,
  separateProfileFields,
  createOrUpdateProfile,
  setupParticipantFeatures,
} from "./helper";
import { getWhiteLabelIdByEventId } from "services/white-label";

//GET
export const getProfileByUserId = async (userId: string) => {
  try {
    const profile = await prisma.profile.findFirst({
      where: {
        userId,
      },
    });
    return profile;
  } catch (error: any) {
    throw new Error(error);
  }
};
export const getProfileByWhitLabelId = async (
  eventId: string,
  search?: string,
  lang?: string
) => {
  try {
    let whereClause: Prisma.ProfileWhereInput = {
      eventId,
    };

    if (!!search && search.length > 0) {
      const isObjectId = /^[0-9a-fA-F]{24}$/.test(search);

      const searchConditions: Prisma.ProfileWhereInput[] = [
        { name: { contains: search, mode: "insensitive" } },
        { email: { contains: search, mode: "insensitive" } },
        { phoneNumber: { contains: search, mode: "insensitive" } },
      ];

      if (isObjectId) {
        searchConditions.push({ orders: { some: { id: search } } });
      }

      whereClause = {
        AND: [
          whereClause,
          {
            OR: searchConditions,
          },
        ],
      };
    }
    const profileCount = await prisma.profile.count({
      where: whereClause,
    });
    // Step 1: fetch profiles (no deep order includes)
    const profiles = await prisma.profile.findMany({
      where: whereClause,
      include: {
        event: true,
      },
    });

    // Step 2: batch-fetch orders for these profiles (keeps response shape while avoiding heavy includes per-profile)
    const profileIds = profiles.map((p) => p.id);
    const orders =
      profileIds.length === 0
        ? []
        : await prisma.order.findMany({
            where: {
              profileId: { in: profileIds },
            },
            include: {
              //TODO: check completed orders
              orderItems: {
                include: {
                  ticket: {
                    include: {
                      bookableItem: {
                        include: {
                          translations: lang
                            ? {
                                where: {
                                  language: {
                                    code: lang,
                                  },
                                },
                              }
                            : true,
                        },
                      },
                    },
                  },
                  checksInOut: true,
                },
              },
            },
          });

    const ordersByProfileId = orders.reduce<Record<string, typeof orders>>(
      (acc, order) => {
        const pid = order.profileId;
        if (!pid) return acc;
        (acc[pid] ||= []).push(order);
        return acc;
      },
      {}
    );

    const profilesWithOrders = profiles.map((profile) => ({
      ...profile,
      orders: ordersByProfileId[profile.id] ?? [],
    }));
    return {
      profiles: profilesWithOrders.map((profile) => ({
        ...profile,
        tickets: profile.orders.flatMap((order) =>
          order.orderItems
            .map((item) => flattenTicketWithBookableItem(item.ticket as any))
            .filter(Boolean)
        ),
      })),
      profileCount,
    };
  } catch (error: any) {
    throw new Error(error);
  }
};
export const getProfileByOrderItemId = async (
  orderItemId: string,
  lang?: string
) => {
  try {
    const profile = await prisma.orderItem.findFirst({
      where: {
        id: orderItemId,
      },
      include: {
        ticket: {
          include: {
            bookableItem: {
              include: {
                translations: lang
                  ? {
                      where: {
                        language: {
                          code: lang,
                        },
                      },
                    }
                  : true,

                ticket: true,
              },
            },
          },
        },
        checksInOut: true,
        order: {
          include: {
            profile: true,
          },
        },
      },
    });
    return {
      ...profile,
      ticket: flattenTicketWithBookableItem(
        profile?.ticket?.bookableItem as any
      ),
    };
  } catch (error: any) {
    throw new Error(error);
  }
};
export const getProfileByUserDataAndWhitLabel = async (
  email: string,
  eventId: string,
  phoneNumber: string,
  countryCode: string,
  userId?: string,
  groupId?: string
) => {
  const or: any[] = [];

  if (phoneNumber && countryCode && eventId) {
    or.push({ AND: [{ phoneNumber }, { countryCode }, { eventId }] });
  }

  if (email && eventId) {
    or.push({ AND: [{ email }, { eventId }] });
  }

  if (userId) {
    or.push({ userId });
  }

  if (groupId) {
    or.push({
      participantGroups: { some: { id: groupId } },
    });
  }
  // If nothing to filter by, prevent a broad query
  if (or.length === 0) {
    return null; // or throw new Error('No filter provided');
  }
  try {
    const profile = await prisma.profile.findFirst({
      where: {
        OR: or,
      },
    });
    return profile;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const getProfileByEventIdAndUserEmail = async (
  eventId: string,
  email: string
) => {
  return await prisma.profile.findFirst({
    where: { email, eventId },
  });
};

export const getProfileByEventIdAndUserId = async (
  eventId: string,
  userId: string
) => {
  return await prisma.profile.findFirst({ where: { userId, eventId } });
};

export const getLatestCompletedOrderByEventIdAndUserId = async (
  eventId: string,
  userId: string
) => {
  return await prisma.order.findFirst({
    where: {
      eventId,
      status: OrderStatus.COMPLETED,
      profile: {
        userId,
        eventId,
      },
      orderItems: {
        some: {},
        every: {
          status: OrderItemStatus.CONFIRMED,
        },
      },
    },
    orderBy: [{ completedAt: "desc" }, { updatedAt: "desc" }],
    select: {
      id: true,
      profile: {
        select: {
          id: true,
          userId: true,
          name: true,
          email: true,
          phoneNumber: true,
          company: true,
          jobTitle: true,
        },
      },
    },
  });
};

export const getCheckoutListByEvent = async (eventId: string) => {
  try {
    const list = await prisma.contactBookGroup.findFirst({
      where: {
        eventId,
        type: ContactBookGroupType.CHECKOUT,
      },
    });
    return list;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const getDoorManagementListByEvent = async (eventId: string) => {
  try {
    const list = await prisma.contactBookGroup.findFirst({
      where: {
        eventId,
        type: ContactBookGroupType.DOOR_MANAGEMENT,
      },
    });
    return list;
  } catch (error: any) {
    throw new Error(error);
  }
};
//CREATE

export const createProfileV2 = async (
  data: ProfileData,
  eventId: string,
  orderId?: string,
  userId?: string,
  whiteLabelId?: string,
  orderType?: OrderType,
  anonymous?: boolean
): Promise<CreateProfileV2Result> => {
  try {
    // Check if profile already exists
    const existingProfile = await findExistingProfile(
      data.email,
      data.phoneNumber,
      eventId,
      userId
    );

    if (existingProfile && orderType !== OrderType.INVITATION) {
      console.log("Profile already exists");
      return { profile: existingProfile };
    }

    // Separate known fields from extra data
    const profileData = separateProfileFields(data);

    // Handle participant group features if applicable
    let token: string | undefined;
    let entityType: ParticipantType | undefined;
    let participantGroupIds: string[] = [];
    if (orderId) {
      participantGroupIds = await getParticipantGroupsByOrderId(orderId);
    }

    // Create or update profile
    const profile = await createOrUpdateProfile(
      existingProfile,
      profileData,
      eventId,
      orderId,
      participantGroupIds[0],
      orderType,
      userId,
      anonymous
    );
    if (participantGroupIds.length > 0) {
      const result = await setupParticipantFeatures(
        profile,
        participantGroupIds,
        eventId,
        whiteLabelId,
        userId
      );
      token = result.token;
      entityType = result.entityType;
    }

    return {
      profile,
      ...(token && { token }),
      ...(entityType && { entityType }),
    };
  } catch (error: any) {
    console.error("Error creating profile:", error);
    throw new Error(error.message || "Failed to create profile");
  }
};
export const createProfile = async (
  data: any,
  eventId: string,
  orderId?: string,
  listType?: ContactBookGroupType,
  userId?: string,
  listId?: string,
  fromTemplateId?: string,
  participantGroupIds?: string[],
  isInvitation?: boolean
) => {
  try {
    const allowedFields = DEFAULT_FIELDS.map((field) => field.value);
    const getParticipatGroups = await prisma.participantGroup.findMany({
      where: {
        id: {
          in: participantGroupIds,
        },
      },
    });

    const participantGroupTypes = getParticipatGroups.map(
      (group) => group.type
    );
    // Separate known fields and extraData
    const profileData: Record<string, any> = {};
    const extraData: Record<string, any> = {};

    for (const key in data) {
      if (allowedFields.includes(key)) {
        profileData[key] = normalizeProfileFieldValue(key, data[key]);
      } else {
        extraData[key] = data[key]; // Store unknown fields in extraData
      }
    }
    // Attach user if provided

    // Attach extraData if it has content
    if (Object.keys(extraData).length > 0) {
      profileData.extraData = extraData;
    }

    const event = await getEvent(eventId);
    if (listId) {
      const checkIfProfileExistsOnContactBookGroup =
        await getProfileByGroupIdAcrossInvitationCategories(
          listId,
          profileData.email,
          profileData.phoneNumber
        );

      if (checkIfProfileExistsOnContactBookGroup) {
        throw new Error("Profile already exists");
      }
    }

    const profile = await prisma.profile.create({
      data: {
        ...profileData,
        event: {
          connect: {
            id: eventId,
          },
        },

        participantGroups: participantGroupIds
          ? {
              connect: participantGroupIds.map((id) => ({ id })),
            }
          : undefined,
        orders: orderId
          ? {
              connect: {
                id: orderId,
              },
            }
          : undefined,
        contactBookGroup: listId
          ? {
              connect: {
                id: listId,
              },
            }
          : {
              create: {
                type: listType ?? undefined,
                name: `${listType} ${
                  event?.translations ? event.translations[0].name : ""
                } `,
                fields: {
                  create: {
                    type: FormEntityType.CONTACT_BOOK_GROUP,
                    formTemplate: fromTemplateId
                      ? {
                          connect: { id: fromTemplateId },
                        }
                      : undefined,
                  },
                },
                event: {
                  connect: {
                    id: eventId,
                  },
                },
              },
            },
      },
    });
    if (!isInvitation) {
      const whiteLabelId = await getWhiteLabelIdByEventId(eventId);
      if (!whiteLabelId) {
        throw new Error(WHITE_LABEL_REQUIRED_MESSAGE);
      }
      const user = await findOrCreateScopedUser({
        email: data.email,
        whiteLabelId,
        name: data.name || "",
      });
      await prisma.profile.update({
        where: { id: profile.id },
        data: { userId: user.id },
      });
      let entityType: ParticipantType = participantGroupTypes.includes(
        ParticipantGroupType.EXHIBITORS
      )
        ? ParticipantType.EXHIBITOR
        : participantGroupTypes.includes(ParticipantGroupType.SPEAKERS)
        ? ParticipantType.SPEAKER
        : participantGroupTypes.includes(ParticipantGroupType.SPONSORS)
        ? ParticipantType.SPONSOR
        : participantGroupTypes.includes(ParticipantGroupType.MEDIA)
        ? ParticipantType.MEDIA
        : participantGroupTypes.includes(ParticipantGroupType.SUPPORT)
        ? ParticipantType.SUPPORT
        : ParticipantType.VISITOR;
      if (participantGroupIds?.length) {
        await createDefaultAvailabilities({
          entityId: user.id,
          entityModel: AvailabilitiesModel.USER,
          eventId: eventId,
        });
        await createDefaultNetworkingSettings(
          eventId,
          user.id,
          NetworkingModel.USER,
          user,
          participantGroupIds?.[0]
        );
        const eventLanguages = await getEventLanguagesByEventIdOrEventSlug(
          eventId
        );

        const userFormTranslations = eventLanguages?.map((t) => ({
          name: user?.name || "",
          languageId: t?.id,
        }));

        await createParticipantFormTemplate(
          {
            whiteLabelId: event?.whiteLabelId as string,
            eventId: eventId,
            createdById: user.id,
          },
          userFormTranslations as any,
          {
            json: {
              logoPosition: "right",
              pages: [
                {
                  name: "page1",
                  elements: [
                    {
                      type: "dropdown",
                      name: "question1",
                      title: "Meeting Location",
                      title_ar: "مكان الاجتماع",
                      choices: [
                        {
                          value: "Item 1",
                          text: "Coffee Area",
                          text_ar: "منطقة المقهى",
                        },
                        {
                          value: "Item 2",
                          text: "Booth",
                          text_ar: "جناح العرض",
                        },
                        {
                          value: "Item 3",
                          text: "Networking Area",
                          text_ar: "منطقة التواصل",
                        },
                        {
                          value: "Item 4",
                          text: "Other",
                          text_ar: "غير ذلك",
                        },
                      ],
                    },
                    {
                      type: "text",
                      name: "question4",
                      visibleIf: "{question1} = 'Item 4'",
                      title: "Custom Location",
                      title_ar: "حدد مكان الاجتماع",
                    },
                    {
                      type: "dropdown",
                      name: "question2",
                      title: "Reason",
                      title_ar: "سبب طلب الاجتماع",
                      choices: [
                        {
                          value: "Item 1",
                          text: "Need a mentoring consultation",
                          text_ar: "للحصول على نصائح مهنية",
                        },
                        {
                          value: "Item 2",
                          text: "Chat and idea exchange",
                          text_ar: "للتحدث وتبادل الأفكار",
                        },
                      ],
                    },
                    {
                      type: "comment",
                      name: "question3",
                      title: "Description",
                      title_ar: "وصف اضافي",
                    },
                  ],
                },
              ],
            },
            numberOfQuestions: 4,
          } as any,
          {
            entityID: profile?.id,
            type: FormEntityType.MEETING,
          } as FormEntity
        );
      }
      const token = jwt.sign(
        {
          entityId: user.id,
          email: profile.email,
          entityType,
        },
        process.env.SECRET_HASH_PASSWORD as string
      );

      await prisma.participantInvitation.upsert({
        where: {
          entityId_entityType_email: {
            entityId: user.id,
            entityType,
            email: profile.email as string,
          },
        },
        update: {
          status: InvitationStatus.PENDING,
          entityType,
          entityId: user.id,
          token,
          isOwner: true,
        },
        create: {
          status: InvitationStatus.PENDING,
          entityType,
          entityId: user.id,
          token,
          isOwner: true,
          email: profile.email as string,
        },
      });
      return { ...profile, token, entityType };
    }
    return { ...profile };
  } catch (error: any) {
    console.error("Error creating profile:", error);
    throw error.message || "Failed to create profile";
  }
};

export const createProfiles = async (
  profilesData: any[],
  eventId: string,
  listId?: string
) => {
  try {
    const allowedFields = DEFAULT_FIELDS.map((field) => field.value);

    // Filter out profiles that already exist
    const filteredProfilesData = [];
    const skippedProfiles = [];

    for (const data of profilesData) {
      let existingProfile = null;

      // Check if profile exists using the cross-invitation categories function
      if (listId && (data.email || data.phoneNumber)) {
        try {
          existingProfile = await getProfileByGroupIdAcrossInvitationCategories(
            listId,
            data.email,
            data.phoneNumber
          );
        } catch (error) {
          console.warn("Error checking for existing profile:", error);
          // Continue with creation if check fails
        }
      }

      if (existingProfile) {
        console.log(`Skipping profile creation - already exists:`, {
          email: data.email,
          phoneNumber: data.phoneNumber,
          existingProfileId: existingProfile.id,
        });
        skippedProfiles.push({
          data,
          reason: "already_exists",
          existingProfileId: existingProfile.id,
        });
      } else {
        filteredProfilesData.push(data);
      }
    }

    console.log(
      `Processing ${filteredProfilesData.length} new profiles, skipped ${skippedProfiles.length} existing profiles`
    );

    // If no profiles to create, return early
    if (filteredProfilesData.length === 0) {
      return {
        count: 0,
        skippedCount: skippedProfiles.length,
        skippedProfiles,
        createdProfiles: [],
      };
    }

    // Prepare profiles for bulk creation
    const profiles = filteredProfilesData.map((data) => {
      const profileData: Record<string, any> = {};
      const extraData: Record<string, any> = {};

      for (const key in data) {
        if (allowedFields.includes(key)) {
          // Force phoneNumber to string
          if (key === "phoneNumber" && data[key] != null) {
            profileData[key] = String(data[key]);
          } else {
            profileData[key] = normalizeProfileFieldValue(key, data[key]);
          }
        } else {
          extraData[key] = data[key];
        }
      }

      // Convert objects in extraData to JSON strings
      for (const key in extraData) {
        if (typeof extraData[key] === "object" && extraData[key] !== null) {
          extraData[key] = JSON.stringify(extraData[key]);
        }
      }

      return {
        ...profileData,
        eventId: eventId,
        contactBookGroupId: listId as string,
        extraData: Object.keys(extraData).length > 0 ? extraData : undefined,
      };
    });

    // Use `createMany` for batch insertion
    const createdProfiles = await prisma.profile.createMany({
      data: profiles,
      // skipDuplicates: true, // Additional safety net at database level
    });

    console.log(`Successfully created ${createdProfiles.count} profiles`);

    return {
      count: createdProfiles.count,
      skippedCount: skippedProfiles.length,
      skippedProfiles,
      createdProfiles,
    };
  } catch (error: any) {
    console.error({ error });
    throw new Error(error.message || "Failed to create profiles");
  }
};

//UPDATE
export const updateProfile = async (
  profileId: string,
  data: any,
  orderId?: string
) => {
  try {
    const allowedFields = DEFAULT_FIELDS.map((field) => field.value);
    const profileData: Record<string, any> = {};
    const extraData: Record<string, any> = {};

    for (const key in data) {
      if (allowedFields.includes(key)) {
        profileData[key] = normalizeProfileFieldValue(key, data[key]);
      } else {
        extraData[key] = data[key]; // Store unknown fields in extraData
      }
    }
    if (Object.keys(extraData).length > 0) {
      profileData.extraData = extraData;
    }
    if (
      typeof profileData.email === "string" &&
      profileData.email.trim().length > 0
    ) {
      profileData.email = profileData.email.trim().toLowerCase();
    }
    // ponytail: Profile.email is event-scoped; User.email is WL login.
    // Do NOT sync Profile → User here (unique User_email_accessibleWhiteLabelId_key
    // + multi-event profiles). App-wide rule tracked in clickup_user_vs_profile_email.md.
    const profile = await prisma.profile.update({
      where: { id: profileId },
      data: {
        ...profileData,
        orders: orderId
          ? {
              connect: {
                id: orderId,
              },
            }
          : undefined,
      },
    });
    return profile;
  } catch (error: any) {
    console.error({ error });
    throw new Error(error?.message || error);
  }
};

export const updateProfileByEventId = async (
  userId: string | undefined | null,
  eventId: string,
  data: Prisma.ProfileUpdateInput,
  profileId?: string,
  tags?: string[],
  orderId?: string,
  tx?: Prisma.TransactionClient
) => {
  try {
    const db = tx ?? prisma;
    const selectedUserId = userId || undefined;
    // Profile.email is event-scoped; never mutate the linked User login email.
    if (typeof data.email === "string" && data.email.trim()) {
      data.email = data.email.trim().toLowerCase();
    }
    const nextEmail =
      typeof data.email === "string" && data.email.trim()
        ? data.email.trim().toLowerCase()
        : undefined;

    const profile =
      (profileId
        ? await db.profile.findFirst({
            where: { id: profileId, eventId },
          })
        : null) ??
      (selectedUserId
        ? await db.profile.findFirst({
            where: { userId: selectedUserId, eventId },
            orderBy: { createdAt: "desc" },
          })
        : null);
    const previousEmail = profile?.email?.toLowerCase();
    let linkedUserId = profile?.userId ?? selectedUserId;

    if (nextEmail && previousEmail !== nextEmail) {
      // Indexed: @@index([eventId, email]). No mode:"insensitive" — that COLLSCAN.
      const occupant = await db.profile.findFirst({
        where: {
          eventId,
          email: nextEmail,
          ...(profile?.id ? { id: { not: profile.id } } : {}),
        },
        select: { id: true, userId: true },
      });
      if (
        occupant &&
        eventProfileEmailConflicts({
          occupantProfileId: occupant.id,
          occupantUserId: occupant.userId,
          currentProfileId: profile?.id,
          currentUserId: selectedUserId,
        })
      ) {
        throw new Error(EVENT_PROFILE_EMAIL_TAKEN);
      }

      const whiteLabelId = await getWhiteLabelIdByEventId(eventId);
      const matchingUser = whiteLabelId
        ? await db.user.findUnique({
            where: {
              email_accessibleWhiteLabelId: {
                email: nextEmail,
                accessibleWhiteLabelId: whiteLabelId,
              },
            },
            select: { id: true },
          })
        : null;
      if (matchingUser && matchingUser.id !== linkedUserId) {
        const matchingUserProfile = await db.profile.findFirst({
          where: {
            eventId,
            userId: matchingUser.id,
            ...(profile?.id ? { id: { not: profile.id } } : {}),
          },
          select: { id: true, userId: true },
        });
        if (
          matchingUserProfile &&
          eventProfileEmailConflicts({
            occupantProfileId: matchingUserProfile.id,
            occupantUserId: matchingUserProfile.userId,
            currentProfileId: profile?.id,
            currentUserId: linkedUserId,
          })
        ) {
          throw new Error(EVENT_PROFILE_EMAIL_TAKEN);
        }
        linkedUserId = matchingUser.id;
      }
    }

    if (linkedUserId && tags) {
      const currentUser = await db.user.findUnique({
        where: { id: linkedUserId },
        include: {
          tags: true,
        },
      });

      const currentTagIds = currentUser?.tags.map((t) => t.id) || [];
      const toDisconnect = currentTagIds.filter((id) => !tags.includes(id));
      const toConnect = tags.filter((id) => !currentTagIds.includes(id));

      await db.user.update({
        where: { id: linkedUserId },
        data: {
          tags: {
            disconnect: toDisconnect.map((id) => ({ id })),
            connect: toConnect.map((id) => ({ id })),
          },
          ...(data.name ? { name: data.name } : {}),
          ...(data.birthday ? { dateOfBirth: data.birthday } : {}),
        },
      });
    }

    const targetProfileId = profile?.id;

    const {
      id: _id,
      userId: _userId,
      profileId: _profileId,
      entityId: _entityId,
      entityType: _entityType,
      ...profileData
    } = data as Record<string, unknown>;

    let updatedProfile: Profile | null = null;
    if (targetProfileId) {
      updatedProfile = await db.profile.update({
        where: {
          id: targetProfileId,
        },
        data: {
          ...(profileData as Prisma.ProfileUpdateInput),
          ...(linkedUserId && linkedUserId !== profile.userId
            ? { user: { connect: { id: linkedUserId } } }
            : {}),
          orders: orderId
            ? {
                connect: {
                  id: orderId,
                },
              }
            : undefined,
        },
      });
    } else if (selectedUserId) {
      const profileUserId = linkedUserId ?? selectedUserId;
      const user = await db.user.findUnique({
        where: { id: profileUserId },
      });
      const resolvedEmail =
        typeof data.email === "string" && data.email.trim()
          ? data.email.trim().toLowerCase()
          : user?.email?.toLowerCase() ?? "";
      const resolvedName =
        typeof data.name === "string" && data.name.trim()
          ? data.name.trim()
          : user?.name ?? "";

      const createInput: Prisma.ProfileCreateInput = {
        name: resolvedName,
        email: resolvedEmail,
        phoneNumber:
          typeof data.phoneNumber === "string" ? data.phoneNumber : undefined,
        countryCode:
          typeof data.countryCode === "string" ? data.countryCode : undefined,
        jobTitle: typeof data.jobTitle === "string" ? data.jobTitle : undefined,
        bio: typeof data.bio === "string" ? data.bio : undefined,
        company: typeof data.company === "string" ? data.company : undefined,
        country: typeof data.country === "string" ? data.country : undefined,
        city: typeof data.city === "string" ? data.city : undefined,
        state: typeof data.state === "string" ? data.state : undefined,
        gender: typeof data.gender === "string" ? data.gender : undefined,
        age: typeof data.age === "number" ? data.age : undefined,
        birthday: data.birthday as Date | string | undefined,
        socialMedia: data.socialMedia as
          | Prisma.SocialMediaCreateInput
          | undefined,
        image: typeof data.image === "string" ? data.image : undefined,
        user: { connect: { id: profileUserId } },
        event: { connect: { id: eventId } },
        orders: orderId
          ? {
              connect: {
                id: orderId,
              },
            }
          : undefined,
      };

      updatedProfile = await db.profile.create({
        data: createInput,
      });
    }

    if (typeof data.phoneNumber === "string") {
      const targetUserIds = [
        ...(linkedUserId ? [linkedUserId] : []),
        ...(updatedProfile?.userId ? [updatedProfile.userId] : []),
      ];
      if (targetUserIds.length > 0) {
        await db.user.updateMany({
          where: { id: { in: targetUserIds } },
          data: { phoneNumber: data.phoneNumber },
        });
      }
    }

    if (
      updatedProfile?.id &&
      ("participantGroups" in data || "participantGroupIds" in data)
    ) {
      invalidateCheckinRosterForProfile(updatedProfile.id).catch(
        () => undefined
      );
    }

    return updatedProfile;
  } catch (error) {
    console.error({ error });
    throw error instanceof Error ? error : new Error(String(error));
  }
};

export const disconnectInvitationAttendee = async (
  attendeeId: string,
  categoryId: string
) => {
  try {
    const profile = await prisma.invitationAttendeeCategory.update({
      where: { id: categoryId },
      data: {
        invitationAttendees: {
          disconnect: {
            id: attendeeId,
          },
        },
      },
    });
    return profile;
  } catch (error: any) {
    console.error({ error });
    throw new Error(error);
  }
};
export const getEventByUserId = async (
  userId: string,
  eventId: string,
  lang: string = "en"
) => {
  try {
    // First check if the user has a profile for this event
    const hasProfile = await prisma.profile.findFirst({
      where: {
        userId,
        eventId,
      },
    });

    if (!hasProfile) {
      return null;
    }

    // Now fetch the event with all required includes
    const event = await prisma.event.findFirst({
      where: {
        id: eventId,
      },
      include: {
        translations: {
          where: {
            language: {
              code: lang,
            },
          },
        },
        venues: {
          include: {
            translations: lang
              ? {
                  where: { language: { code: lang } },
                }
              : true,
          },
        },
      },
    });

    return event;
  } catch (error: any) {
    console.error({ error });
    throw new Error(error);
  }
};

export const getEventsByUserId = async (
  userId: string,
  lang: string = "en",
  eventId?: string,
  whiteLabelId?: string,
  pastOrComing?: string
) => {
  try {
    const event = await prisma.event.findMany({
      where: {
        profiles: {
          some: {
            userId,
          },
        },
        ...(whiteLabelId && { whiteLabelId }),
        ...(eventId && { id: eventId }),
        ...(pastOrComing && {
          startDate: {
            [pastOrComing === "past" ? "lte" : "gte"]: new Date(),
          },
        }),
      },
      include: {
        profiles: {
          where: {
            userId,
          },
          select: { active: true },
          orderBy: {
            createdAt: "desc",
          },
          take: 1,
        },
        translations: {
          where: {
            language: {
              code: lang,
            },
          },
          include: {
            language: true,
          },
        },
        venues: {
          include: {
            translations: lang
              ? {
                  where: { language: { code: lang } },
                }
              : true,
          },
        },
      },
    });
    return event;
  } catch (error: any) {
    console.error({ error });
    throw new Error(error);
  }
};

export const getProfileByGroupIdAcrossInvitationCategories = async (
  groupId: string,
  email?: string,
  phoneNumber?: string
) => {
  try {
    console.log({ groupId, email, phoneNumber }, "🤨");

    // First, get the invitation ID from the group
    const contactBookGroup = await prisma.contactBookGroup.findUnique({
      where: { id: groupId },
      include: {
        invitationAttendeeCategories: {
          select: {
            invitationId: true,
          },
        },
      },
    });

    if (
      !contactBookGroup ||
      contactBookGroup.invitationAttendeeCategories.length === 0
    ) {
      console.log("No invitation found for this group");
      return null;
    }

    // Get the invitation ID (assuming all categories belong to the same invitation)
    const invitationId =
      contactBookGroup.invitationAttendeeCategories[0].invitationId;

    // Build the OR conditions dynamically
    const orConditions = [];

    if (email) {
      orConditions.push({ email: email });
    }

    if (phoneNumber) {
      orConditions.push({ phoneNumber: phoneNumber });
    }

    if (orConditions.length === 0) {
      console.log("No search criteria provided");
      return null;
    }

    const profile = await prisma.profile.findFirst({
      where: {
        contactBookGroup: {
          invitationAttendeeCategories: {
            some: {
              invitationId: invitationId,
            },
          },
        },
        OR: orConditions,
      },
      include: {
        contactBookGroup: {
          include: {
            invitationAttendeeCategories: {
              where: {
                invitationId: invitationId,
              },
            },
          },
        },
        user: true,
        participantGroups: true,
      },
    });

    console.log({ profile }, "📋");
    return profile;
  } catch (error: any) {
    console.error({ error });
    return null;
  }
};

// CREATE AUDIENCE PROFILE
// Lightweight function for creating profiles for communication audiences
// Does NOT create users, participant invitations, or other participant setup
export const createAudienceProfile = async (
  data: { name?: string; email: string; phoneNumber?: string },
  eventId: string
): Promise<{
  id: string;
  name: string | null;
  email: string | null;
  phoneNumber: string | null;
}> => {
  try {
    // Check if profile already exists for this event (by email only)
    const existingProfile = await prisma.profile.findFirst({
      where: {
        email: data.email,
        eventId,
      },
      select: {
        id: true,
        name: true,
        email: true,
        phoneNumber: true,
      },
    });

    if (existingProfile) {
      // Update existing profile with new data if provided
      return await prisma.profile.update({
        where: { id: existingProfile.id },
        data: {
          name: data.name || existingProfile.name,
          phoneNumber: data.phoneNumber || existingProfile.phoneNumber,
        },
        select: {
          id: true,
          name: true,
          email: true,
          phoneNumber: true,
        },
      });
    }

    // Create new profile (no user, no participant setup)
    return await prisma.profile.create({
      data: {
        name: data.name || null,
        email: data.email,
        phoneNumber: data.phoneNumber || null,
        eventId,
        active: true,
      },
      select: {
        id: true,
        name: true,
        email: true,
        phoneNumber: true,
      },
    });
  } catch (error: any) {
    console.error("Error creating audience profile:", error);
    throw new Error(error.message || "Failed to create audience profile");
  }
};
